import { useTranslation } from "react-i18next"
import { Link } from "@tanstack/react-router"
import { Badge, Icon, Skeleton, cn } from "@nexus/ui"
import { useRelativeTime } from "@/features/opportunities/lib/format-relative-time"
import type { PersonaUsage, PersonaUsageSet } from "../../types/personas.types"

export interface PersonaUsagePanelProps {
  usage: PersonaUsage | undefined
  isLoading: boolean
  isError: boolean
  /** True when the drawer footer offers the create-set CTA the empty state points at. */
  canCreateSet: boolean
}

/** Baseline-aligned figure + label. Deliberately not a card: this sits inside a drawer
 * that already has the Share-of-Spend surface above it. */
function UsageStat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xl font-semibold leading-none tabular-nums text-foreground">{value}</span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  )
}

function SetStatusBadge({ status }: { status: PersonaUsageSet["status"] }) {
  const { t } = useTranslation()
  // READY is the resting state and carries no badge — a chip on every row would be
  // decoration rather than signal.
  if (status === "GENERATING") {
    return (
      <Badge variant="status-in-production">
        {t("personas.usage.statusGenerating", "Generating")}
      </Badge>
    )
  }
  if (status === "FAILED") {
    return <Badge variant="status-error">{t("personas.usage.statusFailed", "Failed")}</Badge>
  }
  return null
}

function UsageSetRow({ set }: { set: PersonaUsageSet }) {
  const { t } = useTranslation()
  const relativeTime = useRelativeTime(set.createdAt)

  return (
    <Link
      to="/persona-opportunities"
      search={{ setId: set.id, tab: "opportunities", view: "table", status: "all" }}
      className="group/set -mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors duration-150 ease-out hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{set.name}</span>
          <SetStatusBadge status={set.status} />
        </div>
        <span className="text-[11px] text-muted-foreground">{relativeTime}</span>
      </div>

      {set.status === "READY" && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {t("personas.usage.setCounts", "{{opportunities}} angles · {{scripts}} scripts", {
            opportunities: set.opportunityCount,
            scripts: set.scriptCount,
          })}
        </span>
      )}

      <Icon
        name="chevron-right"
        size="sm"
        className="shrink-0 text-muted-foreground/40 transition-colors duration-150 ease-out group-hover/set:text-muted-foreground"
        aria-hidden
      />
    </Link>
  )
}

/**
 * Teaches what a set does with this persona and points at the drawer footer's CTA
 * rather than repeating it — two identically-labelled buttons in one panel would
 * make neither read as the primary action.
 */
function UsageEmptyState({ canCreateSet }: { canCreateSet: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border/60 px-6 py-10 text-center">
      <Icon name="layout-grid" size="lg" className="text-muted-foreground/40" aria-hidden />
      <p className="text-sm font-medium text-foreground">
        {t("personas.usage.emptyTitle", "Not used in any opportunity set yet")}
      </p>
      <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
        {t(
          "personas.usage.emptyBody",
          "An opportunity set turns this persona's pains and motivations into ad angles, then into scripts you can send to a vendor.",
        )}
      </p>
      {canCreateSet && (
        <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
          {t("personas.usage.emptyHint", "Start one with Create Opportunity Set below.")}
        </p>
      )}
    </div>
  )
}

function UsageSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-10">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-5 w-8 rounded" />
            <Skeleton className="h-2.5 w-16 rounded" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-11 w-full rounded-md" />
        ))}
      </div>
    </div>
  )
}

/**
 * "Used in" — the persona's downstream footprint. Totals come from the server
 * uncapped, while `usage.sets` is capped, so the row list can be shorter than
 * `setCount` and says so rather than silently truncating.
 */
export function PersonaUsagePanel({
  usage,
  isLoading,
  isError,
  canCreateSet,
}: PersonaUsagePanelProps) {
  const { t } = useTranslation()

  if (isLoading) return <UsageSkeleton />

  if (isError || !usage) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("personas.usage.loadFailed", "Failed to load where this persona is used")}
      </p>
    )
  }

  if (usage.setCount === 0) {
    return <UsageEmptyState canCreateSet={canCreateSet} />
  }

  const hiddenSetCount = usage.setCount - usage.sets.length

  return (
    <div className="flex flex-col gap-5">
      <div className={cn("flex gap-10 border-b border-border/50 pb-5")}>
        <UsageStat value={usage.setCount} label={t("personas.usage.statSets", "opportunity sets")} />
        <UsageStat
          value={usage.opportunityCount}
          label={t("personas.usage.statOpportunities", "angles")}
        />
        <UsageStat value={usage.scriptCount} label={t("personas.usage.statScripts", "scripts")} />
      </div>

      <div className="flex flex-col divide-y divide-border/40">
        {usage.sets.map((set) => (
          <UsageSetRow key={set.id} set={set} />
        ))}
      </div>

      {hiddenSetCount > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t("personas.usage.moreSets", "Showing the {{shown}} most recent of {{total}} sets", {
            shown: usage.sets.length,
            total: usage.setCount,
          })}
        </p>
      )}
    </div>
  )
}
